import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import client from '../../api/client';
import './AdminPages.css';
import './CustomerRating.css';

// Comparison presets must mirror frontend/src/utils/comparisonPeriods.js
// AND backend/Services/ComparisonPresets.cs. Keep all three in sync.
const COMPARISON_PRESETS = [
  { id: 'last_full_month',     label: 'Last full month vs prior full month' },
  { id: 'last_3_full_months',  label: 'Last 3 full months vs prior 3 full months' },
  { id: 'last_full_quarter',   label: 'Last full quarter vs prior full quarter' },
  { id: 'ytd_full_months',     label: 'YTD vs same period last year' },
  { id: 'last_6_full_months',  label: 'Last 6 full months vs prior 6 full months' },
  { id: 'last_90_days',        label: 'Last 90 days vs prior 90 days' },
];

const ROLLUP_MODES = [
  { id: 'worst',           label: 'Worst-of-children (conservative)' },
  { id: 'weighted_volume', label: 'Weighted by referral volume (a tiny dying sub doesn\'t drag a thriving master to red)' },
];

// Metric weight rows for the editor — order matches the score breakdown.
const METRIC_ROWS = [
  { key: 'Referrals',    volume: 'weightReferralsVolume',   trend: 'weightReferralsTrend',   note: 'Core business value' },
  { key: 'VitalRounds',  volume: 'weightVitalRoundsVolume', trend: 'weightVitalRoundsTrend', note: '' },
  { key: 'Studies',      volume: 'weightStudiesVolume',     trend: 'weightStudiesTrend',     note: 'DICOM throughput' },
  { key: 'Logins',       volume: 'weightLoginsVolume',      trend: 'weightLoginsTrend',      note: 'Sparse / noisy — keep weight low' },
  { key: 'Messages',     volume: 'weightMessagesVolume',    trend: 'weightMessagesTrend',    note: '' },
];

const COLOR_SWATCH = { green: '#10b981', yellow: '#f59e0b', red: '#ef4444', unknown: '#94a3b8' };

export default function CustomerRating() {
  const [policies, setPolicies] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft]   = useState(null);
  const [dirty, setDirty]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [tab, setTab] = useState('formula');

  // Throttle preview recomputes — every weight slider tick would otherwise
  // round-trip the full customer table. 600ms after the last edit feels right.
  const previewTimer = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [policiesRes, activeRes, overridesRes] = await Promise.all([
        client.get('/api/rating-policies'),
        client.get('/api/rating-policies/active').catch(() => ({ data: null })),
        client.get('/api/rating-policies/overrides'),
      ]);
      setPolicies(policiesRes.data || []);
      setActiveId(activeRes.data?.id ?? null);
      setOverrides(overridesRes.data || []);
      setDraft(activeRes.data || policiesRes.data?.[0] || null);
      setDirty(false);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Recompute preview whenever the draft changes. We intentionally call the
  // /preview endpoint with the in-memory draft (not whatever's saved) so the
  // admin sees the *unsaved* formula's effect immediately.
  const runPreview = useCallback(async (policyDraft) => {
    if (!policyDraft) return;
    setPreviewLoading(true);
    try {
      const res = await client.post('/api/rating-policies/preview', policyDraft);
      setPreviewRows(res.data?.customers || []);
    } catch (e) {
      // Preview errors don't block editing — just log.
      console.warn('Preview failed', e);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!draft) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => runPreview(draft), 600);
    return () => clearTimeout(previewTimer.current);
  }, [draft, runPreview]);

  const patch = (changes) => {
    setDraft(d => ({ ...d, ...changes }));
    setDirty(true);
  };

  const handleSelectPolicy = async (id) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setLoading(true);
    try {
      const res = await client.get(`/api/rating-policies/${id}`);
      setDraft(res.data);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      let saved;
      if (draft.id) {
        const res = await client.put(`/api/rating-policies/${draft.id}`, draft);
        saved = res.data;
      } else {
        const res = await client.post('/api/rating-policies', draft);
        saved = res.data;
      }
      setDraft(saved);
      setDirty(false);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsNew = async () => {
    const name = window.prompt('Name for the new policy:', `${draft.name} (copy)`);
    if (!name) return;
    setSaving(true);
    try {
      const payload = { ...draft, id: 0, name, isActive: false };
      const res = await client.post('/api/rating-policies', payload);
      await loadAll();
      handleSelectPolicy(res.data.id);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!draft?.id) return;
    if (dirty && !window.confirm('You have unsaved changes that will NOT be activated. Activate the saved version anyway?')) return;
    setSaving(true);
    try {
      await client.post(`/api/rating-policies/${draft.id}/activate`);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Activation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    if (draft.isActive) { window.alert('Cannot delete the active policy. Activate another first.'); return; }
    if (!window.confirm(`Delete policy "${draft.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await client.delete(`/api/rating-policies/${draft.id}`);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  // ----- override management ------------------------------------------------
  const reloadOverrides = useCallback(async () => {
    setOverridesLoading(true);
    try {
      const res = await client.get('/api/rating-policies/overrides');
      setOverrides(res.data || []);
    } finally {
      setOverridesLoading(false);
    }
  }, []);

  const handleClearOverride = async (id) => {
    if (!window.confirm('Clear this override and let the formula take over?')) return;
    setOverridesLoading(true);
    try {
      await client.post('/api/rating-policies/overrides/clear', [id]);
      await reloadOverrides();
    } catch (e) {
      setError(e.message);
    } finally {
      setOverridesLoading(false);
    }
  };

  const handleClearAllOverrides = async () => {
    if (!overrides.length) return;
    if (!window.confirm(`Clear ALL ${overrides.length} overrides? Each customer will revert to the formula color.`)) return;
    setOverridesLoading(true);
    try {
      await client.post('/api/rating-policies/overrides/clear', overrides.map(o => o.customerId));
      await reloadOverrides();
    } catch (e) {
      setError(e.message);
    } finally {
      setOverridesLoading(false);
    }
  };

  // ----- preview summary stats ----------------------------------------------
  const previewStats = useMemo(() => {
    const buckets = { green: 0, yellow: 0, red: 0, unknown: 0 };
    let changed = 0;
    for (const r of previewRows) {
      buckets[r.draftColor] = (buckets[r.draftColor] || 0) + 1;
      if (r.draftColor !== r.activeColor) changed += 1;
    }
    return { ...buckets, changed, total: previewRows.length };
  }, [previewRows]);

  if (loading && !draft) {
    return <div className="admin-page"><p>Loading rating policies…</p></div>;
  }

  if (!draft) {
    return (
      <div className="admin-page">
        <p>No policies configured. The default policy should be auto-seeded — try restarting the backend.</p>
      </div>
    );
  }

  return (
    <div className="admin-page rating-page">
      <div className="admin-page-header">
        <div>
          <h1>Customer Rating</h1>
          <p className="admin-page-subtitle">
            Tune the weighted formula that decides each customer's red/yellow/green color.
            The active policy applies in real time across My Customers, Insights, and the API.
          </p>
        </div>
        <div className="rating-policy-picker">
          <label>
            <span>Editing:</span>
            <select value={draft.id || ''} onChange={e => handleSelectPolicy(Number(e.target.value))}>
              {policies.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.isActive ? '  (active)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="rating-error">{error}</div>}

      <div className="rating-tabs">
        <button className={tab === 'formula' ? 'active' : ''} onClick={() => setTab('formula')}>Formula</button>
        <button className={tab === 'preview' ? 'active' : ''} onClick={() => setTab('preview')}>
          Live Preview {previewLoading && <span className="rating-spinner">⟳</span>}
        </button>
        <button className={tab === 'overrides' ? 'active' : ''} onClick={() => setTab('overrides')}>
          Overrides {overrides.length > 0 && <span className="rating-pill">{overrides.length}</span>}
        </button>
        <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>History</button>
        <button className={tab === 'docs' ? 'active' : ''} onClick={() => setTab('docs')}>How it works</button>
      </div>

      {tab === 'formula' && (
        <FormulaEditor draft={draft} patch={patch} />
      )}

      {tab === 'preview' && (
        <PreviewPanel
          rows={previewRows}
          loading={previewLoading}
          stats={previewStats}
          dirty={dirty}
        />
      )}

      {tab === 'overrides' && (
        <OverridesPanel
          overrides={overrides}
          loading={overridesLoading}
          onClearOne={handleClearOverride}
          onClearAll={handleClearAllOverrides}
          onRefresh={reloadOverrides}
        />
      )}

      {tab === 'history' && (
        <HistoryPanel
          policies={policies}
          activeId={activeId}
          onSelect={handleSelectPolicy}
        />
      )}

      {tab === 'docs' && <HowItWorksPanel draft={draft} />}

      <div className="rating-action-bar">
        <div className="rating-action-status">
          {dirty && <span className="rating-dirty">● Unsaved changes</span>}
          {draft.isActive && <span className="rating-active-badge">Currently active</span>}
        </div>
        <div className="rating-action-buttons">
          <button onClick={handleSaveAsNew} disabled={saving}>Save as new policy…</button>
          {draft.id && !draft.isActive && (
            <button onClick={handleActivate} disabled={saving} className="rating-btn-activate">Activate</button>
          )}
          {draft.id && !draft.isActive && (
            <button onClick={handleDelete} disabled={saving} className="rating-btn-delete">Delete</button>
          )}
          <button onClick={handleSave} disabled={saving || !dirty} className="rating-btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-panels
// =====================================================================

function FormulaEditor({ draft, patch }) {
  const totalWeight = METRIC_ROWS.reduce((s, r) => s + (Number(draft[r.volume]) || 0) + (Number(draft[r.trend]) || 0), 0)
                    + (Number(draft.weightQbr) || 0);

  return (
    <div className="rating-formula">
      <section className="rating-card">
        <h2>Identity</h2>
        <div className="rating-grid-2">
          <label className="rating-field">
            <span>Policy name</span>
            <input value={draft.name || ''} onChange={e => patch({ name: e.target.value })} />
          </label>
          <label className="rating-field">
            <span>Comparison window</span>
            <select value={draft.comparisonPresetId} onChange={e => patch({ comparisonPresetId: e.target.value })}>
              {COMPARISON_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
        </div>
        <label className="rating-field">
          <span>Notes (optional, internal)</span>
          <textarea rows={2} value={draft.notes || ''} onChange={e => patch({ notes: e.target.value })} />
        </label>
      </section>

      <section className="rating-card">
        <h2>Metric weights</h2>
        <p className="rating-card-hint">
          Each metric contributes a <strong>volume</strong> signal (log-scaled absolute throughput)
          and a <strong>trend</strong> signal (period-over-period direction; up = good).
          Weights are unitless — only their ratios matter. Total weight currently <strong>{totalWeight.toFixed(0)}</strong>.
        </p>
        <table className="rating-weight-table">
          <thead>
            <tr><th>Metric</th><th>Volume weight</th><th>Trend weight</th><th /></tr>
          </thead>
          <tbody>
            {METRIC_ROWS.map(row => (
              <tr key={row.key}>
                <td><strong>{row.key}</strong></td>
                <td><WeightSlider value={draft[row.volume]} onChange={v => patch({ [row.volume]: v })} /></td>
                <td><WeightSlider value={draft[row.trend]} onChange={v => patch({ [row.trend]: v })} /></td>
                <td className="rating-weight-note">{row.note}</td>
              </tr>
            ))}
            <tr className="rating-qbr-row">
              <td><strong>Latest QBR</strong></td>
              <td colSpan={2}><WeightSlider value={draft.weightQbr} onChange={v => patch({ weightQbr: v })} max={50} /></td>
              <td className="rating-weight-note">Heaviest single signal — the human verdict</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="rating-card">
        <h2>QBR scoring</h2>
        <p className="rating-card-hint">
          QBR rating is mapped to a 0-100 signal and decayed with age (a 6-month-old rating contributes half).
        </p>
        <div className="rating-grid-3">
          <label className="rating-field">
            <span>Green at rating ≥</span>
            <input type="number" min={1} max={10} value={draft.qbrGreenAtRating}
                   onChange={e => patch({ qbrGreenAtRating: Number(e.target.value) })} />
          </label>
          <label className="rating-field">
            <span>Yellow at rating ≥</span>
            <input type="number" min={1} max={10} value={draft.qbrYellowAtRating}
                   onChange={e => patch({ qbrYellowAtRating: Number(e.target.value) })} />
          </label>
          <label className="rating-field">
            <span>Age half-life (days)</span>
            <input type="number" min={0} value={draft.qbrAgeHalfLifeDays}
                   onChange={e => patch({ qbrAgeHalfLifeDays: Number(e.target.value) })} />
          </label>
        </div>
      </section>

      <section className="rating-card">
        <h2>Score → color</h2>
        <p className="rating-card-hint">
          Final composite score is 0-100. Above the green threshold = green; above the yellow
          threshold = yellow; otherwise red.
        </p>
        <div className="rating-grid-2">
          <label className="rating-field">
            <span>Green when score ≥</span>
            <input type="number" min={0} max={100} value={draft.greenScoreMin}
                   onChange={e => patch({ greenScoreMin: Number(e.target.value) })} />
          </label>
          <label className="rating-field">
            <span>Yellow when score ≥</span>
            <input type="number" min={0} max={100} value={draft.yellowScoreMin}
                   onChange={e => patch({ yellowScoreMin: Number(e.target.value) })} />
          </label>
        </div>
        <ThresholdBar green={draft.greenScoreMin} yellow={draft.yellowScoreMin} />
      </section>

      <section className="rating-card">
        <h2>Behavior</h2>
        <div className="rating-grid-3">
          <label className="rating-field">
            <span>Master rollup mode</span>
            <select value={draft.masterRollupMode}
                    onChange={e => patch({ masterRollupMode: e.target.value })}>
              {ROLLUP_MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="rating-field">
            <span>Min referrals to be auto-rated</span>
            <input type="number" min={0} value={draft.minSampleReferralsForRating}
                   onChange={e => patch({ minSampleReferralsForRating: Number(e.target.value) })} />
          </label>
          <label className="rating-field">
            <span>New-customer grace period (days)</span>
            <input type="number" min={0} value={draft.newCustomerGracePeriodDays}
                   onChange={e => patch({ newCustomerGracePeriodDays: Number(e.target.value) })} />
          </label>
        </div>
      </section>
    </div>
  );
}

function WeightSlider({ value, onChange, max = 50 }) {
  return (
    <div className="rating-weight-slider">
      <input type="range" min={0} max={max} step={1} value={value}
             onChange={e => onChange(Number(e.target.value))} />
      <input type="number" min={0} max={100} step={1} value={value}
             onChange={e => onChange(Number(e.target.value))}
             className="rating-weight-num" />
    </div>
  );
}

function ThresholdBar({ green, yellow }) {
  const g = Math.max(0, Math.min(100, green));
  const y = Math.max(0, Math.min(100, yellow));
  return (
    <div className="rating-threshold-bar">
      <div className="rating-threshold-segment" style={{ width: `${y}%`, background: COLOR_SWATCH.red }} />
      <div className="rating-threshold-segment" style={{ width: `${Math.max(0, g - y)}%`, background: COLOR_SWATCH.yellow }} />
      <div className="rating-threshold-segment" style={{ width: `${Math.max(0, 100 - g)}%`, background: COLOR_SWATCH.green }} />
      <div className="rating-threshold-marker" style={{ left: `${y}%` }}>{y}</div>
      <div className="rating-threshold-marker" style={{ left: `${g}%` }}>{g}</div>
    </div>
  );
}

function PreviewPanel({ rows, loading, stats, dirty }) {
  return (
    <div className="rating-preview">
      <div className="rating-preview-summary">
        <PreviewBucket color="green"   count={stats.green}   total={stats.total} />
        <PreviewBucket color="yellow"  count={stats.yellow}  total={stats.total} />
        <PreviewBucket color="red"     count={stats.red}     total={stats.total} />
        <PreviewBucket color="unknown" count={stats.unknown} total={stats.total} />
        <div className="rating-preview-changed">
          <strong>{stats.changed}</strong> would change color vs the active policy
        </div>
      </div>

      {dirty && (
        <p className="rating-preview-hint">
          Showing the effect of your unsaved formula. Save or activate to apply.
        </p>
      )}

      {loading && <p>Recomputing…</p>}

      <table className="rating-preview-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Active</th>
            <th>Draft</th>
            <th>Score</th>
            <th>Source</th>
            <th>Top contributors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.customerId} className={r.draftColor !== r.activeColor ? 'rating-row-changed' : ''}>
              <td>
                <strong>{r.name}</strong>
                {r.isMaster   && <span className="rating-tag">Master</span>}
                {r.isSubDeal  && <span className="rating-tag rating-tag-sub">Sub</span>}
                {r.hasOverride && <span className="rating-tag rating-tag-override">Override</span>}
              </td>
              <td><ColorDot color={r.activeColor} /></td>
              <td><ColorDot color={r.draftColor} /></td>
              <td className="rating-score">{r.draftScore.toFixed(1)}</td>
              <td className="rating-source">{r.draftSource}</td>
              <td className="rating-contribs">
                {(r.contributions || [])
                  .filter(c => c.weight > 0)
                  .sort((a, b) => (b.weight * b.score) - (a.weight * a.score))
                  .slice(0, 3)
                  .map(c => <ContribChip key={c.name} c={c} />)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewBucket({ color, count, total }) {
  const pct = total > 0 ? (count / total * 100).toFixed(0) : 0;
  return (
    <div className="rating-preview-bucket">
      <ColorDot color={color} />
      <strong>{count}</strong>
      <span className="rating-preview-bucket-pct">{pct}%</span>
    </div>
  );
}

function ColorDot({ color }) {
  return <span className="rating-color-dot" style={{ background: COLOR_SWATCH[color] || COLOR_SWATCH.unknown }} title={color} />;
}

function ContribChip({ c }) {
  const tone = c.score >= 65 ? 'good' : c.score >= 40 ? 'mid' : 'bad';
  return (
    <span className={`rating-contrib-chip rating-contrib-${tone}`} title={c.note}>
      {c.name} <em>{c.score.toFixed(0)}</em>
    </span>
  );
}

function OverridesPanel({ overrides, loading, onClearOne, onClearAll, onRefresh }) {
  if (loading && !overrides.length) return <p>Loading overrides…</p>;
  return (
    <div className="rating-overrides">
      <div className="rating-overrides-actions">
        <button onClick={onRefresh}>Refresh</button>
        {overrides.length > 0 && (
          <button onClick={onClearAll} className="rating-btn-delete">Clear all overrides</button>
        )}
      </div>
      {overrides.length === 0 ? (
        <p className="rating-empty">No active overrides — every customer is rated by the formula.</p>
      ) : (
        <table className="rating-overrides-table">
          <thead>
            <tr><th>Customer</th><th>Color</th><th>Set at</th><th>Days ago</th><th /></tr>
          </thead>
          <tbody>
            {overrides.map(o => {
              const days = o.setAt ? Math.round((Date.now() - new Date(o.setAt).getTime()) / 86400000) : '—';
              return (
                <tr key={o.customerId}>
                  <td><strong>{o.name}</strong></td>
                  <td><ColorDot color={o.color} /> {o.color}</td>
                  <td>{o.setAt ? new Date(o.setAt).toLocaleDateString() : '—'}</td>
                  <td>{days}</td>
                  <td>
                    <button onClick={() => onClearOne(o.customerId)} className="rating-btn-link">Clear</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function HowItWorksPanel({ draft }) {
  // Pull live values from the draft so the docs reflect what the formula
  // is *currently* configured to do — not just the global defaults. This
  // turns the page into self-explaining documentation.
  const greenAt  = draft?.greenScoreMin ?? 65;
  const yellowAt = draft?.yellowScoreMin ?? 40;
  const qbrGreen = draft?.qbrGreenAtRating ?? 8;
  const qbrYellow = draft?.qbrYellowAtRating ?? 5;
  const halfLife = draft?.qbrAgeHalfLifeDays ?? 180;
  const grace    = draft?.newCustomerGracePeriodDays ?? 14;
  const minRefs  = draft?.minSampleReferralsForRating ?? 5;
  const rollup   = draft?.masterRollupMode ?? 'worst';

  return (
    <div className="rating-docs">
      <section className="rating-card">
        <h2>What this page does</h2>
        <p>
          The active policy decides every customer's red / yellow / green color.
          The same color appears on the My Customers cards, in Insights, and
          anywhere else the API surfaces <code>customer.health.color</code>.
          Edit the formula here, watch the Live Preview update, and Save (or
          Save as new + Activate) when you're happy.
        </p>
      </section>

      <section className="rating-card">
        <h2>Pipeline (in order)</h2>
        <ol className="rating-docs-pipeline">
          <li>
            <strong>Manual override</strong> — if a customer has a color override set on their
            record, that color wins, full stop. Listed on the <em>Overrides</em> tab.
          </li>
          <li>
            <strong>Master rollup</strong> — if a customer has sub-deals (children pointing
            at it via <code>ParentCustomerId</code>), its color is computed by combining
            the children's colors per the configured mode (currently <strong>{rollup}</strong>).
          </li>
          <li>
            <strong>New-customer grace</strong> — if the customer is within {grace} days
            of their effective start date (Contract start when set, else import date)
            <em> AND</em> has fewer than {minRefs} referrals in the comparison window,
            they're reported as <ColorChip c="unknown" /> with source <code>new</code>.
            Customers with enough activity are rated immediately regardless of tenure.
          </li>
          <li>
            <strong>Insufficient data</strong> — if past the grace check but still under
            {minRefs} referrals in the window, they're <ColorChip c="unknown" /> with
            source <code>insufficient-data</code>.
          </li>
          <li>
            <strong>Score</strong> — otherwise, compute a 0-100 weighted score from
            all enabled signals and map to color via the score-to-color thresholds
            (see below).
          </li>
        </ol>
      </section>

      <section className="rating-card">
        <h2>Per-metric signals</h2>
        <p className="rating-card-hint">
          Each of the five metrics (Referrals, VitalRounds, Studies, Logins, Messages)
          contributes <strong>two</strong> signals scored 0-100. Both are combined into the
          final score through the weights you set on the Formula tab.
        </p>

        <h3>Volume signal — log-scaled absolute throughput</h3>
        <pre className="rating-formula-block">
{`volScore = log10(current + 1) / log10(saturation + 1) × 100   // clamped 0..100

saturation = perDayRate × windowDays   // per-metric defaults below`}
        </pre>
        <table className="rating-docs-table">
          <thead><tr><th>Metric</th><th>Per-day saturation</th><th>What "saturated" means</th></tr></thead>
          <tbody>
            <tr><td>Referrals</td>   <td>2 / day</td><td>~180 in a 3-month window scores 100</td></tr>
            <tr><td>VitalRounds</td> <td>0.5 / day</td><td>~45 in a 3-month window scores 100</td></tr>
            <tr><td>Studies</td>     <td>1 / day</td><td>~90 in a 3-month window scores 100</td></tr>
            <tr><td>Logins</td>      <td>5 / day</td><td>~450 in a 3-month window scores 100</td></tr>
            <tr><td>Messages</td>    <td>5 / day</td><td>~450 in a 3-month window scores 100</td></tr>
          </tbody>
        </table>
        <p className="rating-card-hint">
          Log scaling means a customer hits 50% of their saturated score
          well below saturation — meaningful activity is rewarded fast,
          and the curve flattens so a giant customer doesn't dwarf a
          medium one in the rankings.
        </p>

        <h3>Trend signal — period-over-period direction</h3>
        <pre className="rating-formula-block">
{`delta      = (current − previous) / max(previous, 1)   // clamped to [−1, +1]
trendScore = (delta + 1) × 50                          // 0..100`}
        </pre>
        <ul className="rating-docs-bullets">
          <li>Up 100% (or more) vs prior window → <strong>100</strong> (full green)</li>
          <li>Flat (no change) → <strong>50</strong> (yellow midpoint)</li>
          <li>Down 100% → <strong>0</strong> (full red)</li>
          <li>Brand-new activity (prior was 0, current &gt; 0) → <strong>100</strong></li>
        </ul>
      </section>

      <section className="rating-card">
        <h2>QBR signal</h2>
        <p className="rating-card-hint">
          A QBR (Quarterly Business Review) is a CSM-logged meeting with a 1-10 rating.
          The latest QBR for a customer feeds the formula. If no QBR exists, the score
          is metrics-only (the QBR slot is shown as "No QBR on file" in the breakdown).
        </p>
        <pre className="rating-formula-block">
{`if rating >= ${qbrGreen}                  → rawScore = 100
else if rating >= ${qbrYellow}             → rawScore = 50 + (rating − ${qbrYellow}) × 50 / (${qbrGreen} − ${qbrYellow})
else                          → rawScore = rating × 50 / ${qbrYellow}

decay      = 0.5 ^ (ageDays / ${halfLife})
qbrScore   = rawScore × decay`}
        </pre>
        <p className="rating-card-hint">
          With a {halfLife}-day half-life, a {qbrGreen}/10 rating from {halfLife} days ago
          contributes 50 points; from {halfLife * 2} days ago, 25 points; and so on.
          Override the half-life to 0 to disable decay entirely.
        </p>
      </section>

      <section className="rating-card">
        <h2>Combining signals</h2>
        <pre className="rating-formula-block">
{`finalScore = Σ(weight_i × score_i) / Σ(weight_i)   // weighted average, 0..100`}
        </pre>
        <p className="rating-card-hint">
          Weights are <strong>unitless ratios</strong> — only their proportions matter.
          A weight of <code>30</code> next to a weight of <code>10</code> means that signal
          counts three times as much, regardless of whether the others are <code>5</code>
          or <code>50</code>. The renormalization happens automatically, so doubling
          every weight on the page would change nothing.
        </p>
        <p className="rating-card-hint">
          Setting a weight to <code>0</code> disables that signal entirely. If every
          weight is zero (or every signal is missing), the customer is reported as
          <ColorChip c="unknown" /> with source <code>no-signals</code> rather than
          a misleading red.
        </p>
      </section>

      <section className="rating-card">
        <h2>Score → color</h2>
        <pre className="rating-formula-block">
{`if finalScore >= ${greenAt}  → green
if finalScore >= ${yellowAt}  → yellow
otherwise          → red`}
        </pre>
        <ThresholdBar green={greenAt} yellow={yellowAt} />
      </section>

      <section className="rating-card">
        <h2>Worked example</h2>
        <p className="rating-card-hint">
          Say a customer in a 90-day window has 45 referrals (vs 30 prior),
          150 logins (vs 200 prior), and a 7/10 QBR from 60 days ago, with the
          policy weighting referrals-trend = 25, referrals-volume = 15,
          logins-trend = 5, logins-volume = 3, QBR = 30 (other metrics = 0):
        </p>
        <pre className="rating-formula-block">
{`Referrals volume: log10(46) / log10(180+1) × 100      = 73.0     × 15 = 1094
Referrals trend:  ((45−30)/30 → +0.5 → +1) × 50       = 75.0     × 25 = 1875
Logins volume:    log10(151) / log10(450+1) × 100     = 82.1     ×  3 =  246
Logins trend:     ((150−200)/200 → −0.25 → +1) × 50   = 37.5     ×  5 =  188
QBR rawScore:     (7−${qbrYellow}) × 50 / (${qbrGreen}−${qbrYellow}) + 50 = 83.3
QBR decay:        0.5 ^ (60/${halfLife})                       = 0.794
QBR score:                                            = 66.1     × 30 = 1983
                                                                        ─────
                                                          totalWeight = 78
finalScore = 5386 / 78 = 69.1                                          → green`}
        </pre>
      </section>
    </div>
  );
}

function ColorChip({ c }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
      <ColorDot color={c} /> <code>{c}</code>
    </span>
  );
}

function HistoryPanel({ policies, activeId, onSelect }) {
  return (
    <div className="rating-history">
      <table className="rating-history-table">
        <thead>
          <tr><th>Name</th><th>Status</th><th>Created</th><th>Updated</th><th /></tr>
        </thead>
        <tbody>
          {policies.map(p => (
            <tr key={p.id} className={p.id === activeId ? 'rating-row-active' : ''}>
              <td><strong>{p.name}</strong>{p.notes && <div className="rating-history-notes">{p.notes}</div>}</td>
              <td>{p.isActive ? <span className="rating-active-badge">Active</span> : 'Inactive'}</td>
              <td>{new Date(p.createdAt).toLocaleDateString()}</td>
              <td>{new Date(p.updatedAt).toLocaleDateString()}</td>
              <td>
                <button onClick={() => onSelect(p.id)} className="rating-btn-link">Open</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
